/**
 * One in-flight request per sequence, newest wins.
 *
 * Every `setState` a response wants to make — including clearing `loading` — must first ask
 * `isStale()`. Without that, a slow response repopulates a list its own request no longer owns:
 * pick source A, search, switch to B, and A's late answer refills the picker with A's records,
 * which then import under B's id and B's provenance. Superseding also aborts the previous
 * request, so the stale response usually never arrives at all.
 */
export const createRequestSequence = () => {
  let latest = 0;
  let controller: AbortController | null = null;

  /** Abandons the in-flight request: aborts it, and makes its `isStale()` true. */
  const supersede = () => {
    controller?.abort();
    controller = null;
    latest += 1;
  };

  return {
    /** Issues the newest request. Pass `signal` to `fetch`, and guard every write with `isStale`. */
    begin: () => {
      supersede();
      const id = latest;
      controller = new AbortController();

      return { signal: controller.signal, isStale: () => id !== latest };
    },
    /** Supersedes the in-flight request without issuing a new one (e.g. the source changed). */
    cancel: supersede,
  };
};
